import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import Layout from './components/Layout'
import LoadingSpinner from './components/ui/LoadingSpinner'

// Pages
import LoginPage from './pages/auth/LoginPage'
import PendingApprovalPage from './pages/auth/PendingApprovalPage'
import AccountRejectedPage from './pages/auth/AccountRejectedPage'
import PendingUsersPage from './pages/admin/PendingUsersPage'
import DashboardPage from './pages/DashboardPage'
import PersonsPage from './pages/persons/PersonsPage'
import PersonDetailPage from './pages/persons/PersonDetailPage'
import PersonFormPageWizard from './pages/persons/PersonFormPageWizard'
import BiodataPage from './pages/biodata/BiodataPage'
import PhotosPage from './pages/photos/PhotosPage'
import MediaReleaseFormsPage from './pages/media-release-forms/MediaReleaseFormsPage'
import NotFoundPage from './pages/NotFoundPage'
import UsersPage from './pages/admin/UsersPage'
import { useAuthorization } from './hooks/useAuthorization'
import { useAuthorities, AuthorityConstants, type Authority } from './hooks/useAuthorities'

// Route-level gate: ROLE_USER and ROLE_USER_ALL are read-only; they must not
// reach the person-create / person-edit form even by typing the URL.
function RequireAuthorities({ authorities, children }: { authorities: Authority[]; children: React.ReactNode }) {
  const { hasAnyAuthority } = useAuthorities()
  if (!hasAnyAuthority(authorities)) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400">
            You don't have permission to perform this action. Read-only access only.
          </p>
        </div>
      </div>
    )
  }
  return <>{children}</>
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Approval gate — every protected route bounces non-APPROVED users to the
  // matching status page. The backend enforces this too; this is just to keep
  // them from seeing a flash of the UI before the first 403 lands.
  if (user.approval_status === 'PENDING') {
    return <Navigate to="/pending-approval" replace />
  }
  if (user.approval_status === 'REJECTED') {
    return <Navigate to="/account-rejected" replace />
  }

  return <Layout>{children}</Layout>
}

function ApproverRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  const { canApproveUsers } = useAuthorization()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  if (!user) return <Navigate to="/login" replace />
  if (user.approval_status === 'PENDING') return <Navigate to="/pending-approval" replace />
  if (user.approval_status === 'REJECTED') return <Navigate to="/account-rejected" replace />
  if (!canApproveUsers) return <Navigate to="/dashboard" replace />
  return <Layout>{children}</Layout>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  const { isAdmin } = useAuthorization()
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  
  // If no user, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (user.approval_status === 'PENDING') {
    return <Navigate to="/pending-approval" replace />
  }
  if (user.approval_status === 'REJECTED') {
    return <Navigate to="/account-rejected" replace />
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />
  }
  return <Layout>{children}</Layout>
}

function StatusRoute({
  status, children,
}: { status: 'PENDING' | 'REJECTED'; children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  if (!user) return <Navigate to="/login" replace />
  if (user.approval_status !== status) {
    // If user got approved while sitting on this page, push them onward.
    return <Navigate to={user.approval_status === 'APPROVED' ? '/dashboard'
      : user.approval_status === 'REJECTED' ? '/account-rejected'
      : '/pending-approval'} replace />
  }
  return <>{children}</>
}

// Public Route Component (redirect if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (user) {
    if (user.approval_status === 'PENDING') return <Navigate to="/pending-approval" replace />
    if (user.approval_status === 'REJECTED') return <Navigate to="/account-rejected" replace />
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <div className="min-h-screen" style={{ background: 'var(--bk-bg)', color: 'var(--bk-ink)' }}>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Approval-status pages — authenticated but bypass the approval gate.
            Logged-out users get bounced to /login; logged-in approved users
            get bounced to the dashboard so they never see these by mistake. */}
        <Route path="/pending-approval" element={<StatusRoute status="PENDING"><PendingApprovalPage /></StatusRoute>} />
        <Route path="/account-rejected" element={<StatusRoute status="REJECTED"><AccountRejectedPage /></StatusRoute>} />

        {/* Protected Routes */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/persons" 
          element={
            <ProtectedRoute>
              <PersonsPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/persons/:id" 
          element={
            <ProtectedRoute>
              <PersonDetailPage />
            </ProtectedRoute>
          } 
        />
        
        <Route
          path="/persons/new"
          element={
            <ProtectedRoute>
              <RequireAuthorities authorities={[AuthorityConstants.ADMIN, AuthorityConstants.USER_ADMIN, AuthorityConstants.USER_ADMIN_D]}>
                <PersonFormPageWizard />
              </RequireAuthorities>
            </ProtectedRoute>
          }
        />

        <Route
          path="/persons/:id/edit"
          element={
            <ProtectedRoute>
              <RequireAuthorities authorities={[AuthorityConstants.ADMIN, AuthorityConstants.USER_ADMIN, AuthorityConstants.USER_ADMIN_D]}>
                <PersonFormPageWizard />
              </RequireAuthorities>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/biodata"
          element={
            <ProtectedRoute>
              <BiodataPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/photos" 
          element={
            <ProtectedRoute>
              <PhotosPage />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/media-release-forms" 
          element={
            <ProtectedRoute>
              <MediaReleaseFormsPage />
            </ProtectedRoute>
          } 
        />
        

        {/* Admin */}
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <UsersPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users/pending"
          element={
            <ApproverRoute>
              <PendingUsersPage />
            </ApproverRoute>
          }
        />

        {/* Default Routes */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  )
}

export default App
